import { BehaviorSubject, Observable, map, of } from 'rxjs'
import { GenericLayer, StoreConnectors } from './generic-layer'
import { LayerType } from './layer-types'
import { Store } from '@ngrx/store'
import { selectionActions } from '../../../state/selection/selection.actions'
import { WatchableValue } from '../../../case-viewer/neuroglancer-frame/trackable.service'
import { percentToValue, valueToPercent } from '../../utils/color-utils'
import { SelectionLayerInfo, SelectionType } from '../../../state/selection/selection.state'

export class SelectionLayer implements GenericLayer {
    readonly type = LayerType.Selection
    readonly source = undefined
    private storeConnectors: StoreConnectors
    private _selection$: BehaviorSubject<SelectionLayerInfo>

    private get _selection(): SelectionLayerInfo {
        return this._selection$.value
    }

    get name(): string | undefined {
        return this._selection.name
    }

    get nameOrDefault(): string {
        let name = this._selection.name
        if (!name) {
            return 'Unsaved selection'
        }
        if (this._selection.modified) {
            name = name + ' (modified)'
        }
        return name
    }

    setName(newName: string): void {
        this._selection.name = newName
        this.storeConnectors.dispatchLayerName?.(this.name, newName)
    }

    level: number
    setLevel(level: number): void {
        this.level = level
    }

    visible$: Observable<boolean>
    setVisible(visible: boolean): void {
        if (this._selection.annotationVisible === visible) return
        this.storeConnectors.dispatchVisible?.(this._selection.name, visible)
    }
    toggleVisible(): void {
        this.setVisible(!this._selection.annotationVisible)
    }

    readonly opacityPercent: WatchableValue<number>

    readonly opacityUnselectedPercent = null
    readonly saturationPercent = null
    readonly brightness = null
    readonly contrast = null
    readonly normalize = null
    readonly segmentationColor = null

    readonly shaderColor: WatchableValue<string>
    readonly hoverLabel$ = of(null)
    readonly groupName: string | undefined

    public updateFromStore(selection: SelectionLayerInfo): void {
        this._selection$.next(selection)
    }

    constructor(selection: SelectionLayerInfo, private readonly store: Store) {
        this._selection$ = new BehaviorSubject(selection)
        this.storeConnectors = createSelectionLayerStoreConnectors(
            this.store,
            this._selection.type,
            this._selection.rootName
        )

        this.visible$ = this._selection$.pipe(map(selection => selection.annotationVisible))

        this.opacityPercent = {
            value$: this._selection$.pipe(
                map(selection => valueToPercent(selection.annotationOpacity))
            ),
            setValue: percent =>
                this.storeConnectors.dispatchOpacity?.(this.name, percentToValue(percent)),
        }

        this.shaderColor = {
            value$: this._selection$.pipe(map(selection => selection.annotationColor)),
            setValue: value => this.storeConnectors.dispatchShaderColor?.(this.name, value),
        }

        this.groupName = this._selection.rootName
    }
}

const setVisibilityClosure = (
    store: Store,
    selectionType: SelectionType,
    selectionRoot: string | undefined
): ((selectionName: string, visible: boolean) => void) => {
    return (selectionName: string, visible: boolean): void => {
        store.dispatch(
            selectionActions.setSelectionVisibility({
                selectionName,
                selectionType,
                selectionRoot,
                visible,
            })
        )
    }
}

const setOpacityClosure = (
    store: Store,
    selectionType: SelectionType,
    selectionRoot: string | undefined
): ((selectionName: string, opacity: number) => void) => {
    return (selectionName: string, opacity: number): void => {
        store.dispatch(
            selectionActions.setSelectionOpacity({
                selectionName,
                selectionType,
                selectionRoot,
                opacity,
            })
        )
    }
}

const setShaderColorClosure = (
    store: Store,
    selectionType: SelectionType,
    selectionRoot: string | undefined
): ((selectionName: string, color: string) => void) => {
    return (selectionName: string, color: string): void => {
        store.dispatch(
            selectionActions.setSelectionShaderColor({
                selectionName,
                selectionType,
                selectionRoot,
                color,
            })
        )
    }
}

export const createSelectionLayerStoreConnectors = (
    store: Store,
    selectionType: SelectionType,
    selectionRoot: string | undefined
): StoreConnectors => ({
    dispatchVisible: setVisibilityClosure(store, selectionType, selectionRoot),
    dispatchOpacity: setOpacityClosure(store, selectionType, selectionRoot),
    dispatchShaderColor: setShaderColorClosure(store, selectionType, selectionRoot),
})
